import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useState } from "react";
import { FiImage, FiX } from "react-icons/fi";
import { AlertMsg } from "../../../shared/components/alert-msg";
import {
  CreateStoreGroupPayload,
  StoreGroupDetail,
  UpdateStoreGroupPayload,
} from "../../../types/app/storeGroupsTypes";
import { MediaPageProps } from "../../../types/app/mediaTypes";
import StoreTypeImageDrawer from "./StoreTypeImageDrawer";
import StoreTypeImagePreview from "./StoreTypeImagePreview";
import "./StoreTypeFormModal.scss";

type StoreTypeFormModalMode = "create" | "edit";

interface StoreTypeFormValues {
  name: string;
  description: string;
  imageUrl: string;
}

interface MutationRequestState {
  request_status?: string;
  is_request_processing?: boolean;
  request_data?: {
    error?: string;
  };
}

interface StoreTypeFormModalProps {
  show: boolean;
  mode: StoreTypeFormModalMode;
  onClose: () => void;
  initialGroup?: StoreGroupDetail | null;
  onCreate: (payload: CreateStoreGroupPayload) => any;
  onUpdate: (payload: UpdateStoreGroupPayload) => any;
  mutationRequest: MutationRequestState;
  mediaPageProps: Pick<
    MediaPageProps,
    "getImageGalleryAction" | "getImageGalleryRequest"
  >;
}

const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required("Store type is required"),
  description: Yup.string().trim().required("Description is required"),
  imageUrl: Yup.string().trim().required("Image is required"),
});

const StoreTypeFormModal = ({
  show,
  mode,
  onClose,
  initialGroup,
  onCreate,
  onUpdate,
  mutationRequest,
  mediaPageProps,
}: StoreTypeFormModalProps) => {
  const [showImageDrawer, setShowImageDrawer] = useState(false);

  if (!show) {
    return null;
  }

  const initialValues: StoreTypeFormValues = {
    name: initialGroup?.name || "",
    description: initialGroup?.description || "",
    imageUrl: initialGroup?.imageUrl || initialGroup?.logoUrl || "",
  };

  return (
    <>
      <div className="store-type-form-overlay">
        <div className="store-type-form-modal">
          <div className="store-type-form-header">
            <h3>{mode === "create" ? "Create store type" : "Edit store type"}</h3>
            <button type="button" onClick={onClose} className="close-button">
              <FiX size={20} />
            </button>
          </div>

          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values) => {
              if (mode === "create") {
                return onCreate({
                  name: values.name.trim(),
                  description: values.description.trim(),
                  imageUrl: values.imageUrl.trim(),
                });
              }

              if (!initialGroup?.id) {
                return;
              }

              return onUpdate({
                id: initialGroup.id,
                name: values.name.trim(),
                description: values.description.trim(),
                imageUrl: values.imageUrl.trim(),
              });
            }}
          >
            {({ values, setFieldValue }) => (
              <Form className="store-type-form-body">
                {mutationRequest?.request_data?.error && (
                  <AlertMsg
                    type="error"
                    message={mutationRequest.request_data.error}
                  />
                )}

                <div className="form-group">
                  <label htmlFor="name">Store type</label>
                  <Field
                    id="name"
                    name="name"
                    placeholder="Enter a store type, e.g. African"
                    className="input-field"
                  />
                  <ErrorMessage name="name" component="div" className="error-text" />
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <Field
                    id="description"
                    name="description"
                    placeholder="Enter a description"
                    className="input-field"
                  />
                  <ErrorMessage
                    name="description"
                    component="div"
                    className="error-text"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="imageUrl">Image URL</label>
                  <Field
                    id="imageUrl"
                    name="imageUrl"
                    placeholder="Paste an image URL or choose from the library"
                    className="input-field"
                  />
                  <ErrorMessage
                    name="imageUrl"
                    component="div"
                    className="error-text"
                  />
                </div>

                <div className="image-library-row">
                  <button
                    type="button"
                    className="library-button"
                    onClick={() => setShowImageDrawer(true)}
                  >
                    <FiImage size={16} />
                    Check library
                  </button>
                </div>

                <div className="selected-image-preview">
                  <StoreTypeImagePreview
                    imageUrl={values.imageUrl}
                    name={values.name || initialGroup?.name || "Store type"}
                    className="modal-preview-card"
                  />
                  <div className="selected-image-copy">
                    <strong>Selected image</strong>
                    <span>
                      {values.imageUrl
                        ? "This image will be submitted with the store type."
                        : "No image selected yet."}
                    </span>
                  </div>
                </div>

                <button
                  type="submit"
                  className="submit-button"
                  disabled={mutationRequest?.is_request_processing}
                >
                  {mutationRequest?.is_request_processing
                    ? mode === "create"
                      ? "Creating..."
                      : "Saving..."
                    : mode === "create"
                      ? "Create store type"
                      : "Save changes"}
                </button>

                <StoreTypeImageDrawer
                  show={showImageDrawer}
                  onClose={() => setShowImageDrawer(false)}
                  onSelect={(imageUrl) => {
                    setFieldValue("imageUrl", imageUrl);
                    setShowImageDrawer(false);
                  }}
                  currentImageUrl={values.imageUrl}
                  currentName={values.name || initialGroup?.name || "Store type"}
                  pageProps={mediaPageProps}
                />
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default StoreTypeFormModal;
